import React, { useEffect, useState } from 'react';
import './PendingOrdersCard.css';
import StarsRoundedIcon from "@mui/icons-material/StarsRounded";
import axios from 'axios';
import { EnvironmentRegistry } from '../../../services/registry/environmentRegistry';
import Shimmer from '../../common/Shimmer';
import { useNavigate } from 'react-router-dom';
import { homePageCacheService, CACHE_KEYS, getCachedData, setCachedData } from '../cacheService';
import { getConfigData } from '../../../services/configurationService';
import { useDispatch } from 'react-redux';
import { setDayEndPopup } from '../../../stateManagement/actions/dayEndActions';
import { getDayEndStatusFromSession } from '../../dms_common/DayEndUtils';

interface PendingOrdersData {
  caseQty: number;
  pieceQty: number;
  keyAccounts: number;
  totalOutlets: number;
  otherQty: number;
  pendingOrders: number;
}

interface PendingOrdersCardProps {
  pendingOrders?: number;
  noOfOutlets?: number;
  orderQuantity?: string;
  keyAccounts?: number;
}
let text1: string = "";
let text2: string = "";
const fetchPendingOrders = async (): Promise<PendingOrdersData | null> => {
  try {
    const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
    text1 = ConfigDictionary.homePageConfiguration ? "Bulk confirm the orders in one go!" : "Bulk Invoice the orders in one go!";
    text2 = ConfigDictionary.homePageConfiguration ? "Confirm Orders" : "Invoice Orders";
    const authContext = localStorage.getItem("authContext");
    const user = authContext ? JSON.parse(authContext).user.loginId : '';
    const lob = EnvironmentRegistry.getCurrentLob() || "";
    const url = `https://datanode.saleslens.salescode.ai/pendingOrders?accountId=${lob}&loginId=${encodeURIComponent(user)}`;

    // Create cache key for pending orders data
    const cacheKey = `${CACHE_KEYS.PRODUCT_DATA}_pendingOrders_${user}`;

    // Check cache first
    const cachedData = getCachedData<PendingOrdersData>(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    const response = await axios.get(url, {
      headers: {
        'accept': 'application/json, text/plain, */*',
        'accept-language': 'en-US,en;q=0.9,hi;q=0.8,eo;q=0.7,te;q=0.6',
      }
    });

    // Cache the result for 20 seconds
    setCachedData(cacheKey, response.data);

    return response.data;
  } catch (error) {
    console.error("Error fetching pending orders:", error);
    return null;
  }
};

const formatOrderQuantity = (caseQty: number, pieceQty: number, otherQty: number): string => {
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const parts: string[] = [];
  
  if (caseQty > 0 && Boolean(ConfigDictionary.caseUnitLabel)) {
    parts.push(`${caseQty.toLocaleString()} ${ConfigDictionary.caseUnitLabel}`);
  }
  if (otherQty > 0 && Boolean(ConfigDictionary.otherUnitLabel)) {
    parts.push(`${otherQty.toLocaleString()} ${ConfigDictionary.otherUnitLabel}`);
  }
  if (pieceQty > 0 && Boolean(ConfigDictionary.packUnitLabel)) {
    parts.push(`${pieceQty.toLocaleString()} ${ConfigDictionary.packUnitLabel}`);
  }
  
  return parts.join(' | ') || '0';
};

const PendingOrdersCard: React.FC<PendingOrdersCardProps> = () => {
  const [pendingOrdersData, setPendingOrdersData] = useState<PendingOrdersData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const loadPendingOrders = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchPendingOrders();
        if (data) {
          setPendingOrdersData(data);
        } else {
          setError("Unable to load pending orders data");
        }
      } catch (err) {
        setError("An error occurred while loading data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadPendingOrders();
  }, []);

  const dayEndObj = getDayEndStatusFromSession();
  const handleClick = (task: string) => {
    if (dayEndObj && dayEndObj.isDayEndInProgress){
      dispatch(setDayEndPopup(true));
    }
    if (task === "viewOrders"){
      // Navigate to manage orders page with status=pending, but it will be handled properly there
      navigate('/dashboard/sale/manageOrders?status=pending');
    }
  }


  if (loading) {
    return (
      <div className="pending-orders-card shimmer-card">
        <div className="pending-orders-header">
          <div className="pending-orders-title">
            <div className="pending-orders-icon">
              <Shimmer width="18px" height="18px" borderRadius="50%" />
            </div>
            <Shimmer width="180px" height="20px" />
          </div>
          <Shimmer width="120px" height="36px" className="shimmer-button" />
        </div>

        <div className="pending-orders-subtitle">
          <Shimmer width="100%" height="32px" />
        </div>

        <div className="pending-orders-summary">
          <Shimmer width="150px" height="24px" className="shimmer-title" />
          
          <div className="summary-details">
            <div className="summary-metrics">
              <div className="metric-item">
                <Shimmer width="80px" height="14px" className="shimmer-text" />
                <Shimmer width="60px" height="18px" className="shimmer-text" />
              </div>
              
              <div className="metric-item">
                <Shimmer width="100px" height="14px" className="shimmer-text" />
                <Shimmer width="120px" height="18px" className="shimmer-text" />
              </div>
            </div>

            <div className="key-accounts">
              <Shimmer width="19px" height="19px" borderRadius="50%" />
              <Shimmer width="100px" height="16px" />
            </div>
          </div>
        </div>

        <div className="pending-orders-footer">
          <Shimmer width="100px" height="16px" />
        </div>
      </div>
    );
  }

  // Show error-only UI if we actually have an error
  if (error) {
    return (
      <div className="pending-orders-card">
        <div className="pending-orders-header">
          <div className="pending-orders-title">
            <div className="pending-orders-icon">
              <span style={{ display: 'flex', alignItems: 'center', color: '#D55151' }}>
                <img src={require('../../../assets/images/png/9. Return.svg').default} alt="Danger" style={{ width: 18, height: 18 }} />
              </span>
            </div>
            <span className="pending-orders-count">
              <span style={{ fontWeight: 700 }}>Error</span> Loading pending orders
            </span>
          </div>
        </div>
        <div className="pending-orders-subtitle">
          {error || "Unable to load pending orders data"}
        </div>
      </div>
    );
  }

  // No data UI – styled like this card (similar to out-of-stock message)
  if (!pendingOrdersData || pendingOrdersData.pendingOrders === 0) {
    return (
      <div className="pending-orders-card">
        <div className="pending-orders-header">
          <div className="pending-orders-title">
            <div className="pending-orders-icon">
              <span style={{ display: 'flex', alignItems: 'center', color: '#D55151' }}>
                <img src={require('../../../assets/images/png/9. Return.svg').default} alt="Info" style={{ width: 18, height: 18 }} />
              </span>
            </div>
            <span className="pending-orders-count" style={{ fontWeight: 700 }}>
              No Pending Orders available
            </span>
          </div>
        </div>
      </div>
    );
  }

  const { pendingOrders, totalOutlets, caseQty, pieceQty, otherQty, keyAccounts } = pendingOrdersData;
  const orderQuantityText = formatOrderQuantity(caseQty, pieceQty, otherQty);

  return (
    <div className="pending-orders-card">
      <div className="pending-orders-header">
        <div className="pending-orders-title">
          <div className="pending-orders-icon">
          <span style={{ display: 'flex', alignItems: 'center', color: '#D55151' }}>
            <img src={require('../../../assets/images/png/9. Return.svg').default} alt="Danger" style={{ width: 18, height: 18 }} />
            </span>
          </div>
          <span className="pending-orders-count">
            <span style={{ fontWeight: 700 }}>{pendingOrders.toLocaleString()}</span> Pending orders
          </span>
        </div>
        {pendingOrders > 0 && (
          <button 
            className="pending-orders-btn"
            onClick={() => handleClick("viewOrders")}
          >
            {text2}
          </button>
        )}
      </div>

      <div className="pending-orders-subtitle">
      {text1}
      </div>

      <div className="pending-orders-summary">
        <h3 className="summary-title">{pendingOrders.toLocaleString()} pending orders</h3>
        
        <div className="summary-details">
          <div className="summary-metrics">
            <div className="metric-item">
              <span className="metric-label">No. of outlets</span>
              <span className="metric-value" style={{ fontWeight: 500 }}>{totalOutlets.toLocaleString()}</span>
            </div>
            
            <div className="metric-item">
              <span className="metric-label">Order Quantity</span>
              <span className="metric-value">{orderQuantityText}</span>
            </div>
          </div>

          <div className="key-accounts">
            <div className="key-accounts-icon" style={{marginBottom: "-5px"}}>
            <StarsRoundedIcon
            sx={{
              color: "#FDB42C",
              height: "19px",
              width: "19px",
              borderRadius: "100%",
            }}
          />
            </div>
            <span className="key-accounts-text">
              {keyAccounts > 0 ? `${keyAccounts} Key accounts` : "No Key Accounts"}
            </span>
          </div>
        </div>
      </div>

      {pendingOrders > 0 && (
        <div className="pending-orders-footer">
          <button
            onClick={() => handleClick("viewOrders")}
            className="view-orders-link"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            View orders
          </button>
        </div>
      )}
    </div>
  );
};

export default PendingOrdersCard;